/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.14.1
 */

import { useMemo } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';


import { Checkbox, RadioButton } from '../../../components/common';
import InfoBlock from '../../../components/common/InfoBlock/InfoBlock';
import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { EColorStatus } from '../../../modules/app/appConstants';
import { makeSelectTranslationWizardBillsDoc } from '../../../modules/internationalization/intSelectors';
import { IWizardBillsDocTranslation } from '../../../modules/internationalization/types';
import { IRootReducer } from '../../../reducers/rootReducer';
import { TWizardBillsDocSetParamsPayload } from '../types/wizardBillsDocActionTypes';
import { EWizardBillsDocWithDetailStatus } from '../types/wizardBillsDocTypes';
import { wizardBillsDocSetParamsAction } from '../wizardBillsDocActions';
import { IWizardBillsDocReducer } from '../wizardBillsDocReducer';
import { makeSelectWizardBillsDocParam } from '../wizardBillsDocSelectors';
import { WizardBlockDocStepChooseStyled } from './styled/WizardBlockDocStyled';

const WizardBillsDocStepChoose = ({
                                      formatsAvailable,
                                      formatChoose,
                                      isActOrderAvailable,
                                      isActOrderChecked,
                                      isAdvanceInvoiceAvailable,
                                      isAdvanceInvoiceChecked,
                                      isInvoiceAvailable,
                                      isInvoiceChecked,
                                      isInvoiceOrderAvailable,
                                      isInvoiceOrderChecked,
                                      isOrderChecked,
                                      isOrderWithDetailChecked,
                                      orderWithDetailStatus,
                                      setParams,
                                      translationBillsDoc,
                                  }: IConnectedProps & IDispatchProps) => {

    const orderWithDetailStatusTooltip = useMemo(() =>
        orderWithDetailStatus === EWizardBillsDocWithDetailStatus.disable
        ? translationBillsDoc.chooseDoc.orderWithDetailStatus[EWizardBillsDocWithDetailStatus.disable] : '',
        [orderWithDetailStatus, translationBillsDoc]);

    const isWarningInfoBlockShown = 
        orderWithDetailStatus === EWizardBillsDocWithDetailStatus.partially && isOrderWithDetailChecked;

    return (
        <WizardBlockDocStepChooseStyled>
            <div className="wizard-block-doc__title">
                {translationBillsDoc.chooseDoc.title}
            </div>
            <div className="wizard-block-doc__group-control">
                <Checkbox
                    checked={isOrderChecked}
                    dataName="wizard-bills-doc-report"
                    label={translationBillsDoc.chooseDoc.report}
                    onChange={value => setParams({ isOrderChecked: value })}
                />
                <div className="wizard-block-doc__sub-group">
                    <div className="wizard-block-doc__group-control wizard-block-doc__group-column">
                        <ArrowTooltip title={orderWithDetailStatusTooltip}>
                            <span>
                                <RadioButton
                                    checked={isOrderWithDetailChecked}
                                    dataName="wizard-bills-doc-with-detail"
                                    disabled={!isOrderChecked
                                    || orderWithDetailStatus === EWizardBillsDocWithDetailStatus.disable}
                                    label={translationBillsDoc.chooseDoc.reportWithDetail}
                                    onChange={() => setParams({ isOrderWithDetailChecked: true })}
                                />
                            </span>
                        </ArrowTooltip>
                        {
                            isWarningInfoBlockShown && (
                                <InfoBlock colorStatus={EColorStatus.WARNING}>
                                    {
                                        translationBillsDoc.chooseDoc
                                            .orderWithDetailStatus[EWizardBillsDocWithDetailStatus.partially]
                                    }
                                </InfoBlock>
                            )
                        }
                        <RadioButton
                            checked={!isOrderWithDetailChecked}
                            dataName="wizard-bills-doc-without-detail"
                            disabled={!isOrderChecked}
                            label={translationBillsDoc.chooseDoc.reportWithoutDetail}
                            onChange={() => setParams({ isOrderWithDetailChecked: false })}
                        />
                    </div>
                    {
                        !!formatsAvailable.length && (
                            <div className="wizard-block-doc__group-control">
                                <div className="wizard-block-doc__title">
                                    {translationBillsDoc.chooseDoc.format}
                                </div>
                                <div className="wizard-block-doc__group-row">
                                    {
                                        formatsAvailable.map(format => (
                                            <RadioButton
                                                checked={formatChoose === format}
                                                dataName={`wizard-bills-doc-format-${format}`}
                                                disabled={!isOrderChecked}
                                                label={format}
                                                onChange={() => setParams({ formatChoose: format })}
                                            />
                                        ))
                                    }
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
            {
                // Счет-фактура
                isInvoiceAvailable && (
                    <div className="wizard-block-doc__group-control">
                        <Checkbox
                            checked={isInvoiceChecked}
                            dataName="wizard-bills-doc-invoice"
                            label={translationBillsDoc.chooseDoc.invoice}
                            onChange={value => setParams({ isInvoiceChecked: value })}
                        />
                        {
                            // Исключить авансовые счета-фактуры
                            isAdvanceInvoiceAvailable && (
                                <div className="wizard-block-doc__sub-group">
                                    <Checkbox
                                        checked={isAdvanceInvoiceChecked}
                                        dataName="wizard-bills-doc-invoice-advance"
                                        disabled={!isInvoiceChecked}
                                        label={translationBillsDoc.chooseDoc.invoiceAdvanced}
                                        onChange={value => setParams({ isAdvanceInvoiceChecked: value })}
                                    />
                                </div>
                            )
                        }

                    </div>
                )
            }
            {
                // Счет на оплату
                isInvoiceOrderAvailable && (
                    <div className="wizard-block-doc__group-control">
                        <Checkbox
                            checked={isInvoiceOrderChecked}
                            dataName="wizard-bills-doc-invoice-order"
                            label={translationBillsDoc.chooseDoc.invoiceOrder}
                            onChange={value => setParams({ isInvoiceOrderChecked: value })}
                        />
                    </div>
                )
            }
            {
                // Акт оказанных услуг
                isActOrderAvailable && (
                    <div className="wizard-block-doc__group-control">
                        <Checkbox
                            checked={isActOrderChecked}
                            dataName="wizard-bills-doc-act-order"
                            label={translationBillsDoc.chooseDoc.actOrder}
                            onChange={value => setParams({ isActOrderChecked: value })}
                        />
                    </div>
                )
            }
        </WizardBlockDocStepChooseStyled>
    );
};

type TParams = Pick<IWizardBillsDocReducer,
    | 'formatChoose'
    | 'formatsAvailable'
    | 'isActOrderAvailable'
    | 'isActOrderChecked'
    | 'isAdvanceInvoiceAvailable'
    | 'isAdvanceInvoiceChecked'
    | 'isInvoiceAvailable'
    | 'isInvoiceChecked'
    | 'isInvoiceOrderAvailable'
    | 'isInvoiceOrderChecked'
    | 'isOrderChecked'
    | 'isOrderWithDetailChecked'
    | 'orderWithDetailStatus'>;

export interface IConnectedProps extends TParams {
    translationBillsDoc: IWizardBillsDocTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    formatChoose: makeSelectWizardBillsDocParam('formatChoose'),
    formatsAvailable: makeSelectWizardBillsDocParam('formatsAvailable'),
    isActOrderAvailable: makeSelectWizardBillsDocParam('isActOrderAvailable'),
    isActOrderChecked: makeSelectWizardBillsDocParam('isActOrderChecked'),
    isAdvanceInvoiceAvailable: makeSelectWizardBillsDocParam('isAdvanceInvoiceAvailable'),
    isAdvanceInvoiceChecked: makeSelectWizardBillsDocParam('isAdvanceInvoiceChecked'),
    isInvoiceAvailable: makeSelectWizardBillsDocParam('isInvoiceAvailable'),
    isInvoiceChecked: makeSelectWizardBillsDocParam('isInvoiceChecked'),
    isInvoiceOrderAvailable: makeSelectWizardBillsDocParam('isInvoiceOrderAvailable'),
    isInvoiceOrderChecked: makeSelectWizardBillsDocParam('isInvoiceOrderChecked'),
    isOrderChecked: makeSelectWizardBillsDocParam('isOrderChecked'),
    isOrderWithDetailChecked: makeSelectWizardBillsDocParam('isOrderWithDetailChecked'),
    orderWithDetailStatus: makeSelectWizardBillsDocParam('orderWithDetailStatus'),
    translationBillsDoc: makeSelectTranslationWizardBillsDoc(),
});

export interface IDispatchProps {
    setParams: (payload: TWizardBillsDocSetParamsPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setParams: payload => dispatch(wizardBillsDocSetParamsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardBillsDocStepChoose);
